// SignalsAdminPage — /hub/admin/signals (Team & Admin §4, Epic 4).
//
// "Now is driven entirely by rows. A seventh thing that needs a person is an
// INSERT. Reordering the page is an UPDATE to rank." This screen toggles
// `is_active` (on/off) and edits `rank` (ordering) on `ops_signals`. Creating a
// signal is deliberately impossible from the UI — a signal is defined by its
// `detect_sql`, which is engineering's to write — so there is NO Add/Rename/Delete
// surface. Only the two controls worth having.
//
// Read + write, frontend-direct (CLIENTHUB_KNOWLEDGE_BASE.md §7): the page calls
// Supabase through src/api/hub/signals.js — components never touch supabase.
// Every read + write resolves auth.uid() itself; the database is the wall, the
// `admin.manage` check below is a convenience (00_CONTRACT.md §6).
//
// Gated by admin.manage (Brian, Aly). A viewer without it (e.g. Charles) still
// reads the list — the config table is staff-readable — but sees the controls
// INERT (disabled, not hidden-with-padlock); both states look deliberate.
// <NotPermitted> renders only if the READ itself gates (the viewer is not a
// current ops_team_members row), which reads as "you may not see this", distinct
// from the success empty state ("there are no signals").
//
// Non-negotiables: render, don't compute; never hardcode a signal (all are rows
// and all grow); null is not zero (a null rank reads blank, not 0); the
// "nothing to change" sentence and any gate refusal are surfaced VERBATIM — the
// function is the source of truth, never pre-empted by a client-side diff. A
// detector that fails is recorded and skipped; `last_error` / `last_error_at`
// are shown PROMINENTLY when present — a signal that silently stops working is
// the thing this screen exists to catch (spec §4 hard rule).

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { HubShell } from "../../components/hub/shell";
import { NotPermitted } from "../../components/hub/kit";
import { EmptyState, ErrorState } from "../../components/shared/StateMessage";
import PanelErrorBoundary from "../../components/shared/PanelErrorBoundary";
import { useWorkspace } from "../../contexts/WorkspaceContext";
import { fmtDate } from "../../components/shared/format";
import { getSignals, setSignal } from "../../api/hub/signals";

// Defensive column readers — the table is verified, but degrade rather than
// crash on a drifted/missing field (mirrors CapabilitiesPage / MailboxHealthPage).
const keyOf = (r) => r?.signal_key ?? "—";
const labelOf = (r) => r?.label ?? r?.signal_key ?? "—";
const categoryOf = (r) => r?.category ?? null;
const severityOf = (r) => r?.severity ?? null;
const activeOf = (r) => !!r?.is_active;
const rankOf = (r) => (typeof r?.rank === "number" ? r.rank : null);
const lastErrorOf = (r) => r?.last_error ?? null;
const lastErrorAtOf = (r) => r?.last_error_at ?? null;

// Parse the rank input: empty string → null (blank, not 0 — null is not zero).
const parseRank = (str) => {
  if (str === "" || str == null) return null;
  const n = Number(str);
  return Number.isFinite(n) ? n : null;
};

export default function SignalsAdminPage() {
  const { can } = useWorkspace();
  const permitted = can("admin.manage");

  const [status, setStatus] = useState("loading"); // loading | ready | error | gated
  const [signals, setSignals] = useState([]);
  const [error, setError] = useState(null);
  // Per-row draft of the editable fields, keyed by signal_key. Initialized from
  // the loaded row; a Save diffs the draft against the loaded row and sends ONLY
  // the fields that differ (a single field change sends only that field).
  const [drafts, setDrafts] = useState({});
  const [savingKey, setSavingKey] = useState(null);
  const [rowErrors, setRowErrors] = useState({});

  const initDrafts = useCallback((rows) => {
    const d = {};
    for (const r of rows) {
      d[keyOf(r)] = {
        isActive: activeOf(r),
        rank: rankOf(r) == null ? "" : String(rankOf(r)),
      };
    }
    setDrafts(d);
  }, []);

  const load = useCallback(async () => {
    setStatus("loading");
    setError(null);
    try {
      const rows = await getSignals();
      setSignals(rows);
      initDrafts(rows);
      setRowErrors({});
      setStatus("ready");
    } catch (err) {
      // A gate refusal (the viewer is not a current ops_team_members row) →
      // <NotPermitted>, not a crash. The read itself gates on that.
      const msg = err?.message || "Couldn't load signals.";
      if (/not permitted|not authorised|permission denied|forbidden/i.test(msg)) {
        setStatus("gated");
      } else {
        setError(msg);
        setStatus("error");
      }
    }
  }, [initDrafts]);

  useEffect(() => {
    load();
  }, [load]);

  const setDraftField = useCallback((key, field, value) => {
    setDrafts((d) => ({ ...d, [key]: { ...d[key], [field]: value } }));
  }, []);

  // Save a single row. Diff the draft against the loaded row and send ONLY the
  // fields that differ (a single field change sends only that field — spec S4.2).
  // If NOTHING differs, send neither → fn_signal_set raises "nothing to change",
  // which is surfaced verbatim. We do NOT pre-empt the call with a client-side
  // "nothing changed" message — the function is the source of truth (spec S4.4).
  // On success, refetch the list (the function is the wall; the read is canonical).
  const handleSave = useCallback(
    async (key) => {
      const orig = signals.find((s) => keyOf(s) === key);
      const draft = drafts[key];
      if (!orig || !draft) return;
      const patch = {};
      if (draft.isActive !== activeOf(orig)) patch.isActive = draft.isActive;
      const draftRank = parseRank(draft.rank);
      if (draftRank !== rankOf(orig)) patch.rank = draftRank;

      setSavingKey(key);
      setRowErrors((e) => {
        const { [key]: _drop, ...rest } = e;
        return rest;
      });
      try {
        await setSignal({ signalKey: key, ...patch });
        await load();
      } catch (err) {
        // The function's sentence — gate refusal, "nothing to change", or a DB
        // error — surfaced verbatim (knowledge-base §5).
        setRowErrors((e) => ({ ...e, [key]: err?.message || "Couldn't save." }));
      } finally {
        setSavingKey(null);
      }
    },
    [signals, drafts, load],
  );

  const ordered = useMemo(() => signals, [signals]);
  return (
    <HubShell>
      {status === "gated" ? (
        <div className="mx-auto max-w-5xl px-4 py-6">
          <NotPermitted
            title="Not permitted"
            message="You must be a current team member to read the signal registry."
          />
        </div>
      ) : (
        <div className="mx-auto max-w-5xl px-4 py-6" data-testid="signals-admin-page">
          <header className="mb-6">
            <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Signals
            </h1>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Now is driven entirely by rows. Toggle a signal on or off, or change
              its rank to reorder the page — no deploy. A new signal is a row its
              <code className="mx-1 rounded bg-gray-100 px-1 py-0.5 text-xs text-gray-600 dark:bg-gray-800 dark:text-gray-300">detect_sql</code>
              defines, so add/rename/delete stays with engineering and is not here.
            </p>
          </header>

          {status === "error" ? (
            <ErrorState variant="card" title="Signals" message={error} onRetry={load} />
          ) : status === "loading" ? (
            <p className="text-sm text-gray-400" data-testid="signals-loading">
              Loading signals…
            </p>
          ) : ordered.length === 0 ? (
            <EmptyState variant="card" title="Signals" message="No signals in the registry." />
        ) : (
          <PanelErrorBoundary>
            <ul className="space-y-3">
              {ordered.map((r) => {
                const key = keyOf(r);
                const draft = drafts[key] || { isActive: activeOf(r), rank: "" };
                const lerr = lastErrorOf(r);
                const lerrAt = lastErrorAtOf(r);
                const saving = savingKey === key;
                const rowErr = rowErrors[key];
                const inert = !permitted || saving;
                return (
                  <li
                    key={key}
                    data-testid={`signal-row-${key}`}
                    className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900"
                  >
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900 dark:text-gray-100">
                            {labelOf(r)}
                          </span>
                          {categoryOf(r) && (
                            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-500 dark:bg-gray-800 dark:text-gray-400">
                              {categoryOf(r)}
                            </span>
                          )}
                          {severityOf(r) && (
                            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-500 dark:bg-gray-800 dark:text-gray-400">
                              {severityOf(r)}
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-gray-400">{key}</div>
                      </div>

                      <div className="flex items-center gap-3">
                        {/* on/off toggle (is_active). A write, so disabled (not
                            hidden) for non-holders — both states look deliberate. */}
                        <button
                          type="button"
                          role="switch"
                          aria-checked={draft.isActive}
                          aria-label={`Toggle ${labelOf(r)}`}
                          disabled={inert}
                          onClick={() => setDraftField(key, "isActive", !draft.isActive)}
                          className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                            draft.isActive ? "bg-[#32B1FF]" : "bg-gray-300 dark:bg-gray-700"
                          }`}
                          data-testid={`signal-toggle-${key}`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                              draft.isActive ? "translate-x-4" : "translate-x-0.5"
                            }`}
                          />
                        </button>

                        {/* rank (ordering). Empty = blank, not 0 (null is not zero). */}
                        <label className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                          <span>Rank</span>
                          <input
                            type="number"
                            inputMode="numeric"
                            disabled={inert}
                            value={draft.rank}
                            onChange={(e) => setDraftField(key, "rank", e.target.value)}
                            className="w-16 rounded-md border border-gray-300 px-2 py-1 text-sm tabular-nums text-gray-900 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
                            data-testid={`signal-rank-${key}`}
                          />
                        </label>

                        {permitted && (
                          <button
                            type="button"
                            disabled={saving}
                            onClick={() => handleSave(key)}
                            className="rounded-md border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
                            data-testid={`signal-save-${key}`}
                          >
                            {saving ? "Saving…" : "Save"}
                          </button>
                        )}
                      </div>
                    </div>

                    {/* A failing detector is shown PROMINENTLY — a signal that
                        silently stops working is the thing this screen exists
                        to catch (spec §4 hard rule). */}
                    {lerr && (
                      <div className="mt-3" data-testid={`signal-error-${key}`}>
                        <ErrorState
                          variant="strip"
                          message={
                            lerrAt
                              ? `Detector failing: ${lerr} (last seen ${fmtDate(lerrAt)})`
                              : `Detector failing: ${lerr}`
                          }
                        />
                      </div>
                    )}

                    {rowErr && (
                      <div className="mt-3" data-testid={`signal-save-error-${key}`}>
                        <ErrorState variant="strip" message={rowErr} />
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </PanelErrorBoundary>
        )}
      </div>
      )}
    </HubShell>
  );
}


